import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeContext = createContext(null);

// PLATFORM POLICY: Light mode restored - user can toggle between dark/light
const LIGHT_MODE_ENABLED = true;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user, updateUser } = useAuth();
  // Force dark mode - ignore any saved preferences
  const [theme, setTheme] = useState('dark');
  const [themeConfig, setThemeConfig] = useState(null);

  // Load published theme configuration
  useEffect(() => {
    const loadThemeConfig = async () => {
      try {
        const response = await axios.get(`${API}/theme?draft=false`);
        setThemeConfig(response.data);
        applyThemeConfig(response.data);
      } catch (error) {
        console.error('Failed to load theme config:', error);
        // Use default theme if loading fails
      }
    };

    loadThemeConfig();
  }, []);

  // Force dark mode on mount and override any saved preferences
  useEffect(() => {
    if (!LIGHT_MODE_ENABLED) {
      // Platform policy: Force dark mode
      setTheme('dark');
      applyTheme('dark');
      // Override any saved light mode preference
      localStorage.setItem('theme', 'dark');
    } else {
      // Normal behavior when light mode is enabled
      const savedTheme = localStorage.getItem('theme') || user?.theme || 'dark';
      setTheme(savedTheme);
      applyTheme(savedTheme);
    }
  }, [user]);

  const applyTheme = (newTheme) => {
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const applyThemeConfig = (config) => {
    if (!config) return;

    const root = document.documentElement;
    const style = document.createElement('style');
    style.id = 'dynamic-theme';
    
    // Remove any existing dynamic theme
    const existing = document.getElementById('dynamic-theme');
    if (existing) {
      existing.remove();
    }

    let cssContent = ':root {\n';
    
    // Apply light mode colors
    if (config.light_colors) {
      Object.entries(config.light_colors).forEach(([key, value]) => {
        cssContent += `  --${key.replace(/_/g, '-')}: ${value};\n`;
      });
    }

    cssContent += '}\n\n.dark {\n';
    
    // Apply dark mode colors
    if (config.dark_colors) {
      Object.entries(config.dark_colors).forEach(([key, value]) => {
        cssContent += `  --${key.replace(/_/g, '-')}: ${value};\n`;
      });
    }
    
    cssContent += '}\n';

    // Apply typography and spacing to :root
    if (config.typography || config.spacing) {
      let rootExtra = ':root {\n';
      
      if (config.typography) {
        if (config.typography.font_family) {
          rootExtra += `  font-family: ${config.typography.font_family};\n`;
        }
      }
      
      if (config.spacing) {
        if (config.spacing.radius) {
          rootExtra += `  --radius: ${config.spacing.radius};\n`;
        }
      }
      
      rootExtra += '}\n';
      cssContent += rootExtra;
    }

    style.textContent = cssContent;
    document.head.appendChild(style);
  };

  const toggleTheme = async () => {
    // Platform policy: Dark mode only - toggle is disabled
    if (!LIGHT_MODE_ENABLED) {
      console.log('Theme toggle disabled - dark mode only');
      return;
    }
    
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    if (user) {
      await updateUser({ theme: newTheme });
    }
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    isLightModeEnabled: LIGHT_MODE_ENABLED,
    themeConfig,
    refreshTheme: async () => {
      try {
        const response = await axios.get(`${API}/theme?draft=false`);
        setThemeConfig(response.data);
        applyThemeConfig(response.data);
      } catch (error) {
        console.error('Failed to refresh theme:', error);
      }
    }
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};